import { apiClient } from '../api/client';
import type { TipoDeEntrega } from '@/lib/loja-digital';

/**
 * "Minhas compras" da loja de produtos digitais.
 *
 * Location: src/lib/services
 * Why: a API só devolve ao dono da compra a chave e o conteúdo, e só enquanto
 * o acesso não foi revogado. O arquivo nunca tem endereço fixo: cada download
 * pede um link assinado que vale poucos minutos e conta no limite da compra.
 */

export interface ItemDaBiblioteca {
  accessId: string;
  orderId: string | null;
  productId: string;
  productName: string;
  productImage: string | null;
  kind: TipoDeEntrega;
  purchasedAt: string;
  revoked: boolean;
  file: { version: string; fileName: string; size: number; notes: string | null; updatedAt: string } | null;
  downloads: number;
  downloadLimit: number | null;
  licenseKey: string | null;
  /** A mensagem que a loja escreveu para quem comprou (arquivo ou chave). */
  message: string | null;
}

export interface LinkDeDownload {
  url: string;
  fileName: string;
  expiresInSeconds: number;
}

export const digitalService = {
  async library(storeId: string): Promise<ItemDaBiblioteca[]> {
    const r = await apiClient.get<{ items: ItemDaBiblioteca[] }>(`/public/stores/${storeId}/library`);
    return r.items ?? [];
  },

  async download(storeId: string, accessId: string): Promise<LinkDeDownload> {
    return apiClient.post<LinkDeDownload>(`/public/stores/${storeId}/library/${accessId}/download`);
  },
};

/** Tamanho legível: 1,5 MB, 820 KB. */
export function tamanhoLegivel(bytes: number, locale = 'pt-BR'): string {
  const fmt = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 1 });
  if (bytes >= 1024 * 1024 * 1024) return `${fmt(bytes / (1024 * 1024 * 1024))} GB`;
  if (bytes >= 1024 * 1024) return `${fmt(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${fmt(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/**
 * A mensagem da loja em pedaços: texto puro e links http(s). A tela monta cada
 * pedaço como texto (o React escapa) ou como `<a>`; nada vira HTML.
 */
export function pedacosDoConteudo(texto: string): { tipo: 'texto' | 'link'; valor: string }[] {
  const pedacos: { tipo: 'texto' | 'link'; valor: string }[] = [];
  const regex = /https?:\/\/[^\s<>"']+/g;
  let ultimo = 0;
  for (const m of Array.from(texto.matchAll(regex))) {
    // Pontuação no fim fica fora do link ("veja https://x.com.").
    const link = m[0].replace(/[.,;:!?)\]]+$/, '');
    const inicio = m.index ?? 0;
    if (inicio > ultimo) pedacos.push({ tipo: 'texto', valor: texto.slice(ultimo, inicio) });
    pedacos.push({ tipo: 'link', valor: link });
    ultimo = inicio + link.length;
  }
  if (ultimo < texto.length) pedacos.push({ tipo: 'texto', valor: texto.slice(ultimo) });
  return pedacos;
}
